/**
 * dateUtils.js
 * Shared date validation helpers used across all task creation forms.
 */

/**
 * Returns today's date as YYYY-MM-DD (local time, not UTC).
 * Use as the `min` attribute on date inputs.
 */
export function getMinDate() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Parses a date string into a local Date (no timezone shift).
 * Accepts both "YYYY-MM-DD" and full ISO timestamps like "YYYY-MM-DDTHH:mm:ss.sssZ"
 * returned by the Knex pg driver — strips the time portion before parsing so the
 * date is always interpreted in local time, not UTC.
 */
export function parseLocalDate(str) {
  if (!str) return null;
  const datePart = String(str).split('T')[0]; // strip time component if present
  const [y, m, d] = datePart.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/**
 * Returns an error message string if the chosen due date is invalid, or null if valid.
 *
 * Rules:
 *  - Must not be in the past (before today)
 *  - Must not be a Sunday (getDay() === 0)
 *  - Must not be a Saturday (getDay() === 6)
 *
 * Saturday is allowed only if the organisation explicitly works Saturdays.
 * Current requirement: no Sundays, no past dates.
 */
export function validateDueDate(dateStr) {
  if (!dateStr) return null; // empty = auto-calculated from priority; always valid

  const chosen = parseLocalDate(dateStr);
  if (!chosen) return 'Invalid date.';

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (chosen < today) return 'Due date cannot be in the past.';
  if (chosen.getDay() === 0) return 'Due date cannot be a Sunday.';

  return null;
}
